import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { useCallback, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { KybSubmissionData } from 'apis/legitimizer/types';
import { Typography as T } from 'common/typography';

import { InfoSubRow } from './InfoSubRow';
import MissingFieldError from './MissingFieldError';
import { SectionConfigurationContext } from '../SectionConfigurationContext';
import { countryByCode } from '../countryCodes';
import { useBeneficiaryDetailsFieldsConfigMemo } from '../useFieldsConfigMemo';

type Props = {
  submissionData: KybSubmissionData;
  onEditData: (section: string) => void;
  setHasMissingFields?: (value: boolean) => void;
};

const Content = styled.div`
  text-align: start;
`;

const Legend = styled.legend`
  padding: 24px 0;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SecondaryAction = styled(Button).attrs({
  size: 'small',
  variant: 'lightBlue',
})``;

const Row = styled.span`
  display: block;

  & + * {
    margin-block-start: 16px;
  }
`;

const Line = styled.span`
  display: block;
`;

const Divider = styled.div`
  margin-block-start: 8px;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const ViewRow = FormRow;

const ReviewUbo = ({ submissionData, onEditData, setHasMissingFields }: Props) => {
  const sectionConfiguration = useContext(SectionConfigurationContext);
  const beneficiaryDetailsFieldsConfig = useBeneficiaryDetailsFieldsConfigMemo({
    sectionConfiguration,
    entityUUID: true,
  });
  const intl = useIntl();

  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

  const uboDetails = submissionData.beneficiaries?.uboDetails || [];

  const displayAddress =
    beneficiaryDetailsFieldsConfig.street.visible ||
    beneficiaryDetailsFieldsConfig.postalCode.visible ||
    beneficiaryDetailsFieldsConfig.city.visible ||
    beneficiaryDetailsFieldsConfig.country.visible;

  const beneficiariesConfig = sectionConfiguration.beneficiaries?.uboDetails ?? [];
  if (!sectionConfiguration.enabledSections.includes('beneficiaries') || beneficiariesConfig.length === 0) {
    return null;
  }

  return (
    <Content>
      <Legend>
        <T.Heading6>
          <FormattedMessage id="compliance.kyb.sections.beneficiaries.business-owners.heading" />
        </T.Heading6>
        <SecondaryAction type="button" onClick={() => onEditData('beneficiaries')}>
          <FormattedMessage id="state.edit" />
        </SecondaryAction>
      </Legend>

      <ViewRow label={t('compliance.kyb.sections.beneficiaries.business-owners.business-owner-details.label')}>
        <MissingFieldError shouldShow={uboDetails.length === 0} hasFieldErrorState={setHasMissingFields} />

        {uboDetails.map(ubo => (
          <Row key={ubo.entityUUID}>
            {beneficiaryDetailsFieldsConfig.name.visible && (
              <InfoSubRow
                title={t('compliance.kyb.sections.beneficiaries.business-owners.business-owner-details.name')}
              >
                {ubo.fullName?.value || (
                  <MissingFieldError
                    shouldShow={beneficiaryDetailsFieldsConfig.name.required}
                    hasFieldErrorState={setHasMissingFields}
                  />
                )}
              </InfoSubRow>
            )}
            {displayAddress && (
              <InfoSubRow title={t('compliance.kyb.sections.basic-info.business-information.address')}>
                {beneficiaryDetailsFieldsConfig.street.visible && (
                  <Line>
                    {ubo.streetAddress?.value || (
                      <MissingFieldError
                        shouldShow={beneficiaryDetailsFieldsConfig.street.required}
                        hasFieldErrorState={setHasMissingFields}
                      />
                    )}
                  </Line>
                )}
                {(beneficiaryDetailsFieldsConfig.postalCode.visible || beneficiaryDetailsFieldsConfig.city.visible) && (
                  <Line>
                    {(beneficiaryDetailsFieldsConfig.postalCode.visible && ubo.postalCode?.value) || (
                      <MissingFieldError
                        shouldShow={beneficiaryDetailsFieldsConfig.postalCode.required}
                        hasFieldErrorState={setHasMissingFields}
                      />
                    )}
                    &nbsp;
                    {(beneficiaryDetailsFieldsConfig.city.visible && ubo.city?.value) || (
                      <MissingFieldError
                        shouldShow={beneficiaryDetailsFieldsConfig.city.required}
                        hasFieldErrorState={setHasMissingFields}
                      />
                    )}
                  </Line>
                )}
                {beneficiaryDetailsFieldsConfig.country.visible && (
                  <Line>
                    {countryByCode(ubo.country?.value) || (
                      <MissingFieldError
                        shouldShow={beneficiaryDetailsFieldsConfig.country.required}
                        hasFieldErrorState={setHasMissingFields}
                      />
                    )}
                  </Line>
                )}
              </InfoSubRow>
            )}

            {beneficiaryDetailsFieldsConfig.nationality.visible && (
              <InfoSubRow title={t('compliance.kyb.person-info.nationality')}>
                {countryByCode(ubo.nationality?.value) || (
                  <MissingFieldError
                    shouldShow={beneficiaryDetailsFieldsConfig.nationality.required}
                    hasFieldErrorState={setHasMissingFields}
                  />
                )}
              </InfoSubRow>
            )}
            {beneficiaryDetailsFieldsConfig.nationalId.visible && (
              <InfoSubRow title={t('compliance.kyb.sections.basic-info.business-information.national-id')}>
                {ubo.nationalId?.value || (
                  <MissingFieldError
                    shouldShow={beneficiaryDetailsFieldsConfig.nationalId.required}
                    hasFieldErrorState={setHasMissingFields}
                  />
                )}
              </InfoSubRow>
            )}

            {beneficiaryDetailsFieldsConfig.dateOfBirth.visible && (
              <InfoSubRow
                title={t('compliance.kyb.sections.beneficiaries.business-owners.business-owner-details.date-of-birth')}
              >
                {ubo.dateOfBirth?.value || (
                  <MissingFieldError
                    shouldShow={beneficiaryDetailsFieldsConfig.dateOfBirth.required}
                    hasFieldErrorState={setHasMissingFields}
                  />
                )}
              </InfoSubRow>
            )}
            <Divider />
          </Row>
        ))}
      </ViewRow>
    </Content>
  );
};

export default ReviewUbo;
